import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';

/**
 * Fetch Lead Responses by Email (User)
 */
export const fetchLeadResponses = createAsyncThunk(
  'provisionUser/fetchLeadResponseByEmail',
  async ({ leadEmail }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      //   console.log('leadEmail', leadEmail);

      const response = await axios.get(
        `${config.apiURL}/get_LeadResponsesByEmail`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { leadEmail },
        },
      );

      // ✅ RETURN FULL PAYLOAD
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch lead responses',
      );
    }
  },
);

/**
 * ✅ NEW: Submit Invalid Project Report
 * The timestamp is handled automatically by the backend/database.
 */
export const submitInvalidProjectReport = createAsyncThunk(
  'provisionUser/submitInvalidProjectReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');

      // reportData expects: { leadName, leadEmail, projectId, projectName, issueDescription }
      const response = await axios.post(
        `${config.apiURL}/create_InvalidProjectReport`,
        reportData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to submit report',
      );
    }
  },
);

export const updateLeadResponse = createAsyncThunk(
  'provisionUser/updateLeadResponse',
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');

      const response = await axios.post(
        `${config.apiURL}/update_LeadResponseReport`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save feedback',
      );
    }
  },
);

const provisionUserSlice = createSlice({
  name: 'provisionUser',
  initialState: {
    cycle: [], // ✅ stores response.cycle
    leadData: [], // ✅ stores response.data
    loading: false,
    error: null,

    // ✅ New State for Report Submission
    reportLoading: false,
    reportError: null,
    reportSuccess: false,
  },
  reducers: {
    clearProvisionUserState: (state) => {
      // Clear existing state
      state.cycle = [];
      state.leadData = [];
      state.loading = false;
      state.error = null;

      // Clear report state
      state.reportLoading = false;
      state.reportError = null;
      state.reportSuccess = false;
    },
    // ✅ Helper to reset just the report status (useful for closing modals)
    resetReportState: (state) => {
      state.reportLoading = false;
      state.reportError = null;
      state.reportSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ----------------------------------------------------
      // Existing Logic: Fetch Lead Responses
      // ----------------------------------------------------
      .addCase(fetchLeadResponses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadResponses.fulfilled, (state, action) => {
        state.loading = false;
        state.cycle = action.payload.cycle || [];
        state.leadData = action.payload.data || [];
      })
      .addCase(fetchLeadResponses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ----------------------------------------------------
      // ✅ NEW Logic: Submit Invalid Project Report
      // ----------------------------------------------------
      .addCase(submitInvalidProjectReport.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
        state.reportSuccess = false;
      })
      .addCase(submitInvalidProjectReport.fulfilled, (state) => {
        state.reportLoading = false;
        state.reportSuccess = true;
      })
      .addCase(submitInvalidProjectReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
        state.reportSuccess = false;
      })
      //Lead Response Update
      .addCase(updateLeadResponse.pending, (state) => {
        state.reportLoading = true;
      })
      .addCase(updateLeadResponse.fulfilled, (state) => {
        state.reportLoading = false;
        state.reportSuccess = true;
      })
      .addCase(updateLeadResponse.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
      });
  },
});

export const { clearProvisionUserState, resetReportState } =
  provisionUserSlice.actions;

export default provisionUserSlice.reducer;
